/**
 * Spatial indexing utilities for efficient stop lookups in FRODO
 * 
 * Uses R-tree data structure (via rbush) for O(log S + k) nearest neighbor queries
 * where S = total stops and k = stops within radius.
 */

import RBush from 'rbush';
import type { FixedRouteStop } from '../../types/fixedRoutes';

/**
 * Bounding box for R-tree entries
 */
interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * R-tree entry with stop data
 */
interface SpatialIndexEntry extends BBox {
  stop: FixedRouteStop;
}

/**
 * Spatial index for efficient geographic queries on fixed route stops
 */
export class SpatialIndex {
  private tree: RBush<SpatialIndexEntry>;

  constructor() {
    this.tree = new RBush<SpatialIndexEntry>();
  }

  /**
   * Build spatial index from stops array
   * @param stops Array of fixed route stops to index
   */
  buildIndex(stops: FixedRouteStop[]): void {
    const entries: SpatialIndexEntry[] = stops.map((stop) => ({
      minX: stop.coordinates.lng,
      minY: stop.coordinates.lat,
      maxX: stop.coordinates.lng,
      maxY: stop.coordinates.lat,
      stop,
    }));

    this.tree.load(entries);
  }

  /**
   * Query ALL stops within radius of a center point
   * 
   * This is different from findNearest - it returns ALL stops within the radius,
   * which is required for comprehensive stop pair evaluation in FRODO.
   * 
   * @param center Center point [latitude, longitude]
   * @param radiusMeters Radius in meters
   * @returns Array of stops within radius, sorted by distance (closest first)
   */
  queryWithinRadius(
    center: [number, number],
    radiusMeters: number
  ): FixedRouteStop[] {
    const [centerLat, centerLng] = center;

    // Convert radius from meters to approximate degrees
    // 1 degree latitude ≈ 111,000 meters
    // 1 degree longitude ≈ 111,000 * cos(latitude) meters
    const latRadius = radiusMeters / 111000;
    const lngRadius = radiusMeters / (111000 * Math.cos((centerLat * Math.PI) / 180));

    // Query R-tree with bounding box (slightly larger than radius for safety)
    const bbox: BBox = {
      minX: centerLng - lngRadius * 1.1, // 10% buffer
      minY: centerLat - latRadius * 1.1,
      maxX: centerLng + lngRadius * 1.1,
      maxY: centerLat + latRadius * 1.1,
    };

    const candidates = this.tree.search(bbox);

    // Filter to exact radius using Haversine distance
    // and sort by distance (closest first)
    const stopsWithDistance = candidates
      .map((entry) => ({
        stop: entry.stop,
        distance: this.haversineDistance(
          center,
          [entry.stop.coordinates.lat, entry.stop.coordinates.lng]
        ),
      }))
      .filter((item) => item.distance <= radiusMeters)
      .sort((a, b) => a.distance - b.distance);

    return stopsWithDistance.map((item) => item.stop);
  }

  /**
   * Find nearest stop within maxDistance
   * 
   * Optional helper for simple cases. For FRODO, prefer queryWithinRadius
   * to get ALL stops within radius for comprehensive evaluation.
   * 
   * @param point Point [latitude, longitude]
   * @param maxDistance Maximum distance in meters
   * @returns Nearest stop within maxDistance, or null if none found
   */
  findNearest(
    point: [number, number],
    maxDistance: number
  ): FixedRouteStop | null {
    const stops = this.queryWithinRadius(point, maxDistance);
    return stops.length > 0 ? stops[0] : null;
  }

  /**
   * Calculate Haversine distance between two points in meters
   * @param point1 [latitude, longitude]
   * @param point2 [latitude, longitude]
   * @returns Distance in meters
   */
  private haversineDistance(
    point1: [number, number],
    point2: [number, number]
  ): number {
    const R = 6371000; // Earth's radius in meters
    const toRad = (d: number) => (d * Math.PI) / 180;

    const [lat1, lng1] = point1;
    const [lat2, lng2] = point2;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}

/**
 * Create a new spatial index instance
 * @returns New SpatialIndex instance
 */
export function createSpatialIndex(): SpatialIndex {
  return new SpatialIndex();
}

